import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { SiteHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import uid from "common/utils/uid";
import type { PolyLine2 } from "geom/polyLine2Types";

export enum SiteShapeType {
  DRIVEWAY = "DRIVEWAY",
  SITE_FILL = "SITE_FILL",
  SITE_SUBTRACT = "SITE_SUBTRACT",
  SITE_OVERRIDE = "SITE_OVERRIDE",
  CONTOUR = "CONTOUR",
  ROAD = "ROAD",
  RETAINING_WALL = "RETAINING_WALL",
  LANDSCAPE = "LANDSCAPE",
  WALK = "WALK",
  SIDEWALK = "SIDEWALK",
}

// Base interface for common properties
export interface BaseSiteShape extends FormulaDisabledDocEntityBase<"SITE_SHAPE"> {
  readonly id: PrimaryEntityId<"SITE_SHAPE">;
  readonly type: SiteShapeType;
  readonly parentId: DocEntityParentId<"SITE">;
  readonly geom: PolyLine2;
}

// Shapes that have height
export interface HeightBasedShape extends BaseSiteShape {
  readonly zHeight: number;
  readonly height?: number;
  readonly material?: ExteriorMaterial;
}

export interface ConformalShape extends BaseSiteShape {
  readonly isConformal?: boolean;
  readonly material?: ExteriorMaterial;
}

// Specific shape types
export interface DrivewaySiteShape extends ConformalShape, HeightBasedShape {
  readonly type: SiteShapeType.DRIVEWAY;
}

export interface ContourSiteShape extends HeightBasedShape {
  readonly type: SiteShapeType.CONTOUR;
}

export interface SiteOverrideSiteShape extends HeightBasedShape {
  readonly type: SiteShapeType.SITE_OVERRIDE;
}

export interface SidewalkSiteShape extends BaseSiteShape {
  readonly type: SiteShapeType.SIDEWALK;
}

export interface RoadSiteShape extends ConformalShape, HeightBasedShape {
  readonly type: SiteShapeType.ROAD;
}

export interface SiteFillSiteShape extends HeightBasedShape {
  readonly type: SiteShapeType.SITE_FILL;
}

export interface SiteSubtractSiteShape extends HeightBasedShape {
  readonly type: SiteShapeType.SITE_SUBTRACT;
}

export interface RetainingWallSiteShape extends HeightBasedShape {
  readonly type: SiteShapeType.RETAINING_WALL;
}

export interface LandscapeSiteShape extends BaseSiteShape {
  readonly type: SiteShapeType.LANDSCAPE;
  readonly landscapeType: LandscapeType;
}

export interface WalkSiteShape extends ConformalShape, HeightBasedShape {
  readonly type: SiteShapeType.WALK;
}

// Union type of all site shapes
export type SiteShape =
  | ContourSiteShape
  | DrivewaySiteShape
  | SiteOverrideSiteShape
  | RoadSiteShape
  | SiteFillSiteShape
  | SiteSubtractSiteShape
  | RetainingWallSiteShape
  | LandscapeSiteShape
  | SidewalkSiteShape
  | WalkSiteShape;

export function isDriveway(shape: SiteShape): shape is DrivewaySiteShape {
  return shape.type === SiteShapeType.DRIVEWAY;
}

export function isLandscape(shape: SiteShape): shape is LandscapeSiteShape {
  if (shape.type === SiteShapeType.LANDSCAPE) {
    return true;
  }
  return false;
}

export enum LandscapeType {
  BUSHES = "BUSHES",
  TREES = "TREES",
  SINGLE_TREE = "SINGLE_TREE",
}

export enum RampSide {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export function siteShapeNew(
  parentHandle: SiteHandle,
  input: Partial<Omit<SiteShape, "entityType">> & {
    geom: PolyLine2;
    landscapeType?: LandscapeType;
    material?: ExteriorMaterial;
    zHeight?: number;
    height?: number;
    sidewalkWidth?: number;
    sidewalkRampLength?: number;
    rampSide?: RampSide;
    grassCurbWidth?: number;
    curbHeight?: number;
    isConformal?: boolean;
  },
): SiteShape {
  const baseShape: BaseSiteShape = {
    entityType: "SITE_SHAPE",
    sortIndex: 0,
    id: uid(),
    parentId: parentHandle.id,
    name: input.name || "New Shape",
    type: input.type || SiteShapeType.CONTOUR,
    geom: input.geom || new Float64Array(),
  };

  const heightBasedProps: Partial<HeightBasedShape> = {
    zHeight: input.zHeight || 0,
    height: input.height || 0,
    material: input.material,
  };

  const conformalProps: Partial<ConformalShape> = {
    isConformal: input.isConformal || false,
    material: input.material,
  };

  switch (input.type || SiteShapeType.CONTOUR) {
    case SiteShapeType.LANDSCAPE:
      return {
        ...baseShape,
        type: SiteShapeType.LANDSCAPE,
        landscapeType: input.landscapeType || LandscapeType.BUSHES,
      };
    case SiteShapeType.DRIVEWAY:
      return {
        ...baseShape,
        ...heightBasedProps,
        ...conformalProps,
        type: SiteShapeType.DRIVEWAY,
      } as DrivewaySiteShape;
    case SiteShapeType.WALK:
      return {
        ...baseShape,
        ...heightBasedProps,
        ...conformalProps,
        type: SiteShapeType.WALK,
      } as WalkSiteShape;
    case SiteShapeType.SITE_FILL:
      return {
        ...baseShape,
        ...heightBasedProps,
        type: SiteShapeType.SITE_FILL,
      } as SiteFillSiteShape;
    case SiteShapeType.SITE_SUBTRACT:
      return {
        ...baseShape,
        ...heightBasedProps,
        type: SiteShapeType.SITE_SUBTRACT,
      } as SiteSubtractSiteShape;
    case SiteShapeType.ROAD:
      return {
        ...baseShape,
        ...heightBasedProps,
        ...conformalProps,
        type: SiteShapeType.ROAD,
      } as RoadSiteShape;
    case SiteShapeType.RETAINING_WALL:
      return {
        ...baseShape,
        ...heightBasedProps,
        height: input.height || 36,
        type: SiteShapeType.RETAINING_WALL,
      } as RetainingWallSiteShape;
    case SiteShapeType.SITE_OVERRIDE:
      return {
        ...baseShape,
        ...heightBasedProps,
        type: SiteShapeType.SITE_OVERRIDE,
      } as SiteOverrideSiteShape;
    case SiteShapeType.CONTOUR:
    default:
      return {
        ...baseShape,
        type: SiteShapeType.CONTOUR,
        zHeight: input.zHeight || 0,
      };
  }
}

export function isHeightBasedShape(
  shape: SiteShape,
): shape is HeightBasedShape & { type: Exclude<SiteShapeType, SiteShapeType.SIDEWALK | SiteShapeType.LANDSCAPE> } {
  if (shape.type === SiteShapeType.SIDEWALK) {
    return false;
  }

  if (shape.type === SiteShapeType.LANDSCAPE) {
    return false;
  }

  if (isConformalShape(shape) && shape.isConformal) {
    return false;
  }
  return true;
}

export function isConformalShape(shape: SiteShape): shape is DrivewaySiteShape | RoadSiteShape | WalkSiteShape {
  if (shape.type === SiteShapeType.ROAD || shape.type === SiteShapeType.WALK || shape.type === SiteShapeType.DRIVEWAY) {
    return true;
  }
  return false;
}
